/**
 * Capacity Breakdown Result Component
 *
 * Displays:
 * - Hero metrics (key capacity numbers at a glance)
 * - Waterfall-style capacity breakdown
 * - Loss analysis (parity, padding, overhead, slop, reserved)
 * - Final usable capacity
 * - Practical usable capacity (with free space limit)
 */

import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Divider } from '@mui/material';
import type { CapacityResults, EconomicsResults } from '../../types';
import { TooltipIcon } from '../ui';
import { HeroMetrics, StackedCapacityBar } from './';

interface CapacityBreakdownProps {
  results: CapacityResults;
  economics?: EconomicsResults; // Optional for hero metrics
}

export function CapacityBreakdown({ results, economics }: CapacityBreakdownProps) {
  // Prepare hero metrics
  const heroMetrics = {
    primary: {
      label: 'Practical Usable Capacity',
      value: `${results.practicalUsableTiB.toFixed(2)} TiB`,
      subtitle: `${results.practicalUsablePercent.toFixed(1)}% of raw capacity`,
      color: '#1976d2',
    },
    secondary: [
      {
        label: 'Total Raw',
        value: `${results.rawTiB.toFixed(2)} TiB`,
        color: '#e3f2fd',
      },
      {
        label: 'Efficiency',
        value: `${results.usablePercent.toFixed(1)}%`,
        color: '#bbdefb',
      },
      ...(economics ? [
        {
          label: 'Total Cost',
          value: `$${economics.hardwareCost.toFixed(0)}`,
          color: '#90caf9',
        },
        {
          label: 'Cost/TiB',
          value: `$${economics.costPerUsableTB.toFixed(0)}`,
          color: '#64b5f6',
        },
      ] : []),
    ],
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Capacity Breakdown
      </Typography>

      {/* Hero Metrics - Key numbers at a glance */}
      <HeroMetrics {...heroMetrics} />

      <Divider sx={{ my: 3 }} />

      {/* Stacked Capacity Bar - Visual explanation of "where the space went" */}
      <StackedCapacityBar results={results} />

      <Divider sx={{ my: 3 }} />

      {/* Detailed table for precise values */}
      <TableContainer>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell><strong>Total Raw</strong> <TooltipIcon tooltipKey="rawCapacity" /></TableCell>
              <TableCell align="right">{results.rawTiB.toFixed(2)} TiB</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Parity Loss <TooltipIcon tooltipKey="parityOverhead" /></TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>
                -{results.losses.parity.toFixed(2)} TiB
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>P+1 Padding <TooltipIcon tooltipKey="p1Padding" /></TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>
                -{results.losses.padding.toFixed(2)} TiB
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>ZFS Overhead <TooltipIcon tooltipKey="zfsOverhead" /></TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>
                -{results.losses.overhead.toFixed(2)} TiB
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Slop Space <TooltipIcon tooltipKey="slop" /></TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>
                -{results.losses.slop.toFixed(2)} TiB
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Final Usable</strong> <TooltipIcon tooltipKey="finalUsable" /></TableCell>
              <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {results.finalUsableTiB.toFixed(2)} TiB ({results.usablePercent.toFixed(1)}%)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Reserved (Free Space Limit) <TooltipIcon tooltipKey="freeSpaceLimit" /></TableCell>
              <TableCell align="right" sx={{ color: 'warning.main' }}>
                -{results.reservedTiB.toFixed(2)} TiB
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Practical Usable</strong> <TooltipIcon tooltipKey="practicalUsable" /></TableCell>
              <TableCell align="right" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                {results.practicalUsableTiB.toFixed(2)} TiB ({results.practicalUsablePercent.toFixed(1)}%)
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
